import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { logAccess } from '@/lib/accessLogs';
import { resolveAnalysesToCatalogueIds } from '@/lib/analyseMatcher';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import MedicamentAutocomplete from '@/components/feature/MedicamentAutocomplete';
import AnalyseChecklist, { CATEGORIES_ANALYSES } from '@/components/feature/AnalyseChecklist';
import DossierRoseForm from '@/pages/medecin/dossier-patient/components/DossierRoseForm';
import ConsultationEnCoursModal from '@/components/feature/ConsultationEnCoursModal';
import PrintSingleDocumentModal from '@/components/feature/PrintSingleDocumentModal';
import PrintableBonLaboratoire from '@/components/feature/PrintableBonLaboratoire';
import PrintablePrescription from '@/components/feature/PrintablePrescription';
import PrintableFicheSoin from '@/components/feature/PrintableFicheSoin';
import {
  VOIES_GROUPES,
  getVoie,
  getDefaultConditionnement,
  doseCourte,
  formatLigneOrdonnance,
  formatOrdonnanceDocument,
} from '@/lib/voiesMedicament';
import type { MedicamentLigne } from '@/lib/voiesMedicament';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  sexe: string;
  telephone: string;
  email: string;
  adresse: string;
  groupe_sanguin: string;
  allergies: string;
  maladies_chroniques: string;
  antecedents_medicaux: string;
  antecedents_chirurgicaux: string;
  medicaments_actuels: string;
  notes_medicales: string;
  numero_dossier: string;
}



interface MedicamentEnCours {
  id: string;
  nom: string;
  dosage: string;
}

interface SoinLigne {
  id: string;
  date: string;
  description: string;
}

interface DonneesSupplementaires {
  medicamentsEnCours?: MedicamentEnCours[];
  patientRegulier?: boolean;
  traitementNotes?: string;
  medicamentsPrescrits?: MedicamentLigne[];
  soins?: SoinLigne[];
  showFicheSoin?: boolean;
  showBonLabo?: boolean;
  selectedAnalyses?: string[];
  customAnalyses?: { id: string; label: string }[];
  bonLaboUrgence?: boolean;
  bonLaboAssurance?: boolean;
  bonLaboObs?: string;
  dossierNotes?: string;
  tensionValues?: { bras_gauche: string; bras_droit: string; debout: string; assis: string; coucher: string };
  typeSuivi?: 'medecin' | 'departement';
  suiviDepartement?: string;
  editDossierMode?: boolean;
  dossierEditData?: any;
}

type SectionKey = 'dossier' | 'consultation' | 'traitement' | 'antecedents' | 'examen' | 'diagnostic' | 'bon-labo' | 'fiche-soin' | 'suivi' | 'prescription' | 'imprimer' | 'fin';

interface Section {
  key: SectionKey;
  label: string;
  icon: string;
}

const SECTIONS: Section[] = [
  { key: 'dossier', label: 'Dossier Général', icon: 'ri-folder-user-line' },
  { key: 'consultation', label: 'Consultation', icon: 'ri-stethoscope-line' },
  { key: 'antecedents', label: 'Antécédents', icon: 'ri-health-book-line' },
  { key: 'traitement', label: 'Traitement en cours', icon: 'ri-medicine-bottle-line' },
  { key: 'examen', label: 'Examen', icon: 'ri-search-eye-line' },
  { key: 'diagnostic', label: 'Diagnostic', icon: 'ri-mental-health-line' },
  { key: 'bon-labo', label: 'Bon de Laboratoire', icon: 'ri-microscope-line' },
  { key: 'fiche-soin', label: 'Fiche de Soin', icon: 'ri-nurse-line' },
  { key: 'suivi', label: 'Suivi', icon: 'ri-user-shared-line' },
  { key: 'prescription', label: 'Prescription', icon: 'ri-file-text-line' },
  { key: 'imprimer', label: 'Imprimer', icon: 'ri-printer-line' },
  { key: 'fin', label: 'Fin de consultation', icon: 'ri-check-double-line' },
];

export default function ConsultationPage() {
  const { user, role } = useAuth();
  const { isOnline } = useOnlineStatus();
  const { mutate: offlineMutate } = useOfflineMutation<any>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const patientId = searchParams.get('patientId') || '';
  const rdvId = searchParams.get('rdvId') || null;

  const [patient, setPatient] = useState<Patient | null>(null);
  const [medecinId, setMedecinId] = useState<string>('');
  const [medecinInfo, setMedecinInfo] = useState<any>(null);
  const [consultationId, setConsultationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>('dossier');
  const [dossierRoseMode, setDossierRoseMode] = useState(false);

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoad = useRef(true);
  const skipEnCoursCheck = useRef(false);
  const medecinIdRef = useRef<string>('');
  const isAutoSavingRef = useRef(false);
  const autoSaveQueuedRef = useRef(false);
  const isOnlineRef = useRef(isOnline);
  const consultationRdvIdRef = useRef<string | null>(null);

  // --- DOSSIER GÉNÉRAL ---
  const [dossierNotes, setDossierNotes] = useState('');

  // --- ÉDITION DOSSIER PATIENT ---
  const [editDossierMode, setEditDossierMode] = useState(false);
  const [dossierEditData, setDossierEditData] = useState<{
    telephone: string;
    email: string;
    adresse: string;
    allergies: string;
    maladies_chroniques: string;
    antecedents_medicaux: string;
    antecedents_chirurgicaux: string;
    groupe_sanguin: string;
  }>({ telephone: '', email: '', adresse: '', allergies: '', maladies_chroniques: '', antecedents_medicaux: '', antecedents_chirurgicaux: '', groupe_sanguin: '' });
  const [dossierEditSaving, setDossierEditSaving] = useState(false);

  // --- CONSULTATION ---
  const [dateConsultation, setDateConsultation] = useState(new Date().toISOString().split('T')[0]);
  const [poids, setPoids] = useState('');
  const [taille, setTaille] = useState('');
  const [temperature, setTemperature] = useState('');
  const [tensionValues, setTensionValues] = useState<{ bras_gauche: string; bras_droit: string; debout: string; assis: string; coucher: string }>({ bras_gauche: '', bras_droit: '', debout: '', assis: '', coucher: '' });
  const buildTensionSummary = useCallback((): string | null => {
    const parts: string[] = [];
    if (tensionValues.bras_gauche) parts.push(`BG:${tensionValues.bras_gauche}`);
    if (tensionValues.bras_droit) parts.push(`BD:${tensionValues.bras_droit}`);
    if (tensionValues.debout) parts.push(`Debout:${tensionValues.debout}`);
    if (tensionValues.assis) parts.push(`Assis:${tensionValues.assis}`);
    if (tensionValues.coucher) parts.push(`Couché:${tensionValues.coucher}`);
    return parts.length > 0 ? parts.join(' | ') : null;
  }, [tensionValues]);
  const [saturation, setSaturation] = useState('');
  const [symptomes, setSymptomes] = useState('');

  // --- TRAITEMENT EN COURS ---
  const [medicamentsEnCours, setMedicamentsEnCours] = useState<MedicamentEnCours[]>([{ id: '1', nom: '', dosage: '' }]);
  const [patientRegulier, setPatientRegulier] = useState(false);
  const [traitementNotes, setTraitementNotes] = useState('');

  // --- ANTÉCÉDENTS ---
  const [antecedentsConnus, setAntecedentsConnus] = useState('');

  // --- EXAMEN ---
  const [examen, setExamen] = useState('');

  // --- DIAGNOSTIC ---
  const [diagnostic, setDiagnostic] = useState('');

  // --- BON LABO ---
  const [bonLaboSent, setBonLaboSent] = useState(false);
  const [showBonLabo, setShowBonLabo] = useState(false);
  const [selectedAnalyses, setSelectedAnalyses] = useState<string[]>([]);
  const [customAnalyses, setCustomAnalyses] = useState<{ id: string; label: string; catalogueId?: string }[]>([]);
  const [newCustomAnalyse, setNewCustomAnalyse] = useState('');
  const [showAnalyseSuggestions, setShowAnalyseSuggestions] = useState(false);
  const [bonLaboUrgence, setBonLaboUrgence] = useState(false);
  const [bonLaboAssurance, setBonLaboAssurance] = useState(false);
  const [bonLaboObs, setBonLaboObs] = useState('');
  const [dbCustomAnalyses, setDbCustomAnalyses] = useState<{ nom: string; items: { id: string; label: string }[] }[]>([]);
  const [catalogueEntries, setCatalogueEntries] = useState<{ id: string; code: string; nom: string }[]>([]);

  // --- PRINT INDIVIDUEL ---
  const [printModalType, setPrintModalType] = useState<'bon-labo' | 'ordonnance' | 'fiche-soin' | null>(null);

  // --- PRESCRIPTION ---
  const [medicamentsPrescrits, setMedicamentsPrescrits] = useState<MedicamentLigne[]>([{ id: '1', nom: '', dosageNb: '1', forme: 'comprime', matin: true, midi: false, soir: false, nbBoites: '1', conditionnement: 'plaquette', instructions: '', duree: '' }]);

  // --- FICHE DE SOIN ---
  const [showFicheSoin, setShowFicheSoin] = useState(false);
  const [soins, setSoins] = useState<SoinLigne[]>([{ id: '1', date: dateConsultation, description: '' }]);

  // --- FIN ---
  const [prochainRdv, setProchainRdv] = useState('');
  const [typeSuivi, setTypeSuivi] = useState<'medecin' | 'departement'>('medecin');
  const [suiviVers, setSuiviVers] = useState('');
  const [suiviDepartement, setSuiviDepartement] = useState('');
  const [departements, setDepartements] = useState<string[]>([]);
  const [medecinsList, setMedecinsList] = useState<{ id: string; nom: string; prenom: string; specialite: string }[]>([]);
  const [motFin, setMotFin] = useState('');

  // --- MODAL CONSULTATION EN COURS ---
  const [showConsultationEnCoursModal, setShowConsultationEnCoursModal] = useState(false);
  const [consultationEnCoursPatient, setConsultationEnCoursPatient] = useState('');
  const [consultationsAFermer, setConsultationsAFermer] = useState<string[]>([]);



  const sectionRefs: Record<SectionKey, React.RefObject<HTMLDivElement | null>> = {
    dossier: useRef<HTMLDivElement>(null),
    consultation: useRef<HTMLDivElement>(null),
    traitement: useRef<HTMLDivElement>(null),
    antecedents: useRef<HTMLDivElement>(null),
    examen: useRef<HTMLDivElement>(null),
    diagnostic: useRef<HTMLDivElement>(null),
    'bon-labo': useRef<HTMLDivElement>(null),
    'fiche-soin': useRef<HTMLDivElement>(null),
    suivi: useRef<HTMLDivElement>(null),
    prescription: useRef<HTMLDivElement>(null),
    imprimer: useRef<HTMLDivElement>(null),
    fin: useRef<HTMLDivElement>(null),
  };

  // --- Ref pour éviter les closures périmées dans l'auto-save ---
  const autoSaveDataRef = useRef<{
    dateConsultation: string;
    poids: string;
    taille: string;
    temperature: string;
    tensionValues: { bras_gauche: string; bras_droit: string; debout: string; assis: string; coucher: string };
    saturation: string;
    symptomes: string;
    medicamentsEnCours: MedicamentEnCours[];
    patientRegulier: boolean;
    traitementNotes: string;
    antecedentsConnus: string;
    examen: string;
    diagnostic: string;
    prochainRdv: string;
    suiviVers: string;
    suiviDepartement: string;
    typeSuivi: 'medecin' | 'departement';
    motFin: string;
    medicamentsPrescrits: MedicamentLigne[];
    soins: SoinLigne[];
    showFicheSoin: boolean;
    showBonLabo: boolean;
    selectedAnalyses: string[];
    customAnalyses: { id: string; label: string }[];
    bonLaboUrgence: boolean;
    bonLaboAssurance: boolean;
    bonLaboObs: string;
    dossierNotes: string;
  } | null>(null);

  // Mise à jour du ref à chaque render pour garantir les valeurs les plus récentes
  autoSaveDataRef.current = {
    dateConsultation, poids, taille, temperature, tensionValues, saturation,
    symptomes, medicamentsEnCours, patientRegulier, traitementNotes,
    antecedentsConnus, examen, diagnostic, prochainRdv, suiviVers, suiviDepartement,
    typeSuivi, motFin, medicamentsPrescrits,
    soins, showFicheSoin, showBonLabo, selectedAnalyses, customAnalyses,
    bonLaboUrgence, bonLaboAssurance, bonLaboObs, dossierNotes,
  };

  // Build supplementary data object
  const buildDonneesSupplementaires = useCallback((): DonneesSupplementaires => ({
    medicamentsEnCours,
    patientRegulier,
    traitementNotes,
    medicamentsPrescrits,
    soins,
    showFicheSoin,
    showBonLabo,
    selectedAnalyses,
    customAnalyses,
    bonLaboUrgence,
    bonLaboAssurance,
    bonLaboObs,
    dossierNotes,
    tensionValues,
    typeSuivi,
    suiviDepartement,
  }), [
    medicamentsEnCours, patientRegulier, traitementNotes,
    medicamentsPrescrits,
    soins, showFicheSoin, showBonLabo,
    selectedAnalyses, customAnalyses, bonLaboUrgence, bonLaboAssurance, bonLaboObs,
    dossierNotes, tensionValues, typeSuivi, suiviDepartement,
  ]);

  // Apply supplementary data from loaded JSON
  const applyDonneesSupplementaires = (data: DonneesSupplementaires | null) => {
    if (!data) return;
    if (data.medicamentsEnCours?.length) setMedicamentsEnCours(data.medicamentsEnCours);
    if (data.patientRegulier !== undefined) setPatientRegulier(data.patientRegulier);
    if (data.traitementNotes !== undefined) setTraitementNotes(data.traitementNotes);
    if (data.medicamentsPrescrits?.length) {
      setMedicamentsPrescrits(data.medicamentsPrescrits.map((m) => ({
        ...m,
        forme: m.forme || 'comprime',
        instructions: m.instructions || '',
        duree: m.duree || '',
      })));
    }
    if (data.soins?.length) setSoins(data.soins);
    if (data.showFicheSoin !== undefined) setShowFicheSoin(data.showFicheSoin);
    if (data.showBonLabo !== undefined) setShowBonLabo(data.showBonLabo);
    if (data.selectedAnalyses?.length) setSelectedAnalyses(data.selectedAnalyses);
    if (data.customAnalyses?.length) setCustomAnalyses(data.customAnalyses);
    if (data.bonLaboUrgence !== undefined) setBonLaboUrgence(data.bonLaboUrgence);
    if (data.bonLaboAssurance !== undefined) setBonLaboAssurance(data.bonLaboAssurance);
    if (data.bonLaboObs !== undefined) setBonLaboObs(data.bonLaboObs);
    if (data.dossierNotes !== undefined) setDossierNotes(data.dossierNotes);
    if (data.tensionValues) setTensionValues(data.tensionValues);
    if (data.typeSuivi !== undefined) setTypeSuivi(data.typeSuivi);
    if (data.suiviDepartement !== undefined) setSuiviDepartement(data.suiviDepartement);
  };

  // --- AUTO-SAVE (debounced, avec ref pour éviter closures périmées) ---
  const doAutoSave = useCallback(async (consId: string) => {
    if (isAutoSavingRef.current) {
      // Une sauvegarde est déjà en cours : on mémorise qu'il faudra re-sauvegarder juste après
      autoSaveQueuedRef.current = true;
      return;
    }
    if (!isOnlineRef.current) return; // Skip auto-save offline — latest state captured when terminating
    isAutoSavingRef.current = true;
    
    setAutoSaveStatus('saving');
    try {
      const d = autoSaveDataRef.current;
      if (!d) { setAutoSaveStatus('idle'); return; }

      // Build tension summary from ref (toujours les valeurs les plus récentes)
      const tensionParts: string[] = [];
      if (d.tensionValues.bras_gauche) tensionParts.push(`BG:${d.tensionValues.bras_gauche}`);
      if (d.tensionValues.bras_droit) tensionParts.push(`BD:${d.tensionValues.bras_droit}`);
      if (d.tensionValues.debout) tensionParts.push(`Debout:${d.tensionValues.debout}`);
      if (d.tensionValues.assis) tensionParts.push(`Assis:${d.tensionValues.assis}`);
      if (d.tensionValues.coucher) tensionParts.push(`Couché:${d.tensionValues.coucher}`);
      const tensionSummary = tensionParts.length > 0 ? tensionParts.join(' | ') : null;

      // Build donnees_supplementaires from ref
      const supp: DonneesSupplementaires = {
        medicamentsEnCours: d.medicamentsEnCours,
        patientRegulier: d.patientRegulier,
        traitementNotes: d.traitementNotes,
        medicamentsPrescrits: d.medicamentsPrescrits,
        soins: d.soins,
        showFicheSoin: d.showFicheSoin,
        showBonLabo: d.showBonLabo,
        selectedAnalyses: d.selectedAnalyses,
        customAnalyses: d.customAnalyses,
        bonLaboUrgence: d.bonLaboUrgence,
        bonLaboAssurance: d.bonLaboAssurance,
        bonLaboObs: d.bonLaboObs,
        dossierNotes: d.dossierNotes,
        tensionValues: d.tensionValues,
        typeSuivi: d.typeSuivi,
        suiviDepartement: d.suiviDepartement,
      };

      const suiviDraft = d.typeSuivi === 'departement'
        ? `Département: ${d.suiviDepartement}`
        : (d.suiviVers || null);
      await supabase.from('consultations').update({
        date_consultation: d.dateConsultation,
        poids: d.poids ? parseFloat(d.poids) : null,
        taille: d.taille ? parseFloat(d.taille) : null,
        temperature: d.temperature ? parseFloat(d.temperature) : null,
        tension: tensionSummary,
        saturation: d.saturation ? parseInt(d.saturation) : null,
        symptomes_signes: d.symptomes.trim() || null,
        traitement_en_cours: [
          d.medicamentsEnCours.filter((m: MedicamentEnCours) => m.nom.trim()).map((m: MedicamentEnCours) => `${m.nom}${m.dosage ? ` (${m.dosage})` : ''}`).join('\n'),
          d.traitementNotes || null
        ].filter(Boolean).join('\n\n') || null,
        patient_regulier_medicaments: d.patientRegulier,
        antecedents_medicaux_connus: d.antecedentsConnus || null,
        examen: d.examen || null,
        diagnostic: d.diagnostic.trim() || null,
        prochain_rdv: d.prochainRdv || null,
        suivi_vers: suiviDraft,
        mot_fin: d.motFin || null,
        donnees_supplementaires: supp as any,
        updated_at: new Date().toISOString(),
      }).eq('id', consId);
      setAutoSaveStatus('saved');
    } catch {
      setAutoSaveStatus('error');
    } finally {
      isAutoSavingRef.current = false;
      if (autoSaveQueuedRef.current) {
        autoSaveQueuedRef.current = false;
        window.setTimeout(() => { doAutoSave(consId); }, 150);
      }
    }
  }, []);

  // Trigger auto-save on any form change
  useEffect(() => {
    if (isInitialLoad.current || !consultationId) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      doAutoSave(consultationId);
    }, 800);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [
    consultationId, dateConsultation, poids, taille, temperature, JSON.stringify(tensionValues), saturation,
    JSON.stringify(symptomes), JSON.stringify(medicamentsEnCours), patientRegulier,
    traitementNotes, antecedentsConnus, examen, diagnostic, prochainRdv, suiviVers, motFin,
    JSON.stringify(medicamentsPrescrits),
    JSON.stringify(soins), showFicheSoin, showBonLabo,
    JSON.stringify(selectedAnalyses), JSON.stringify(customAnalyses), bonLaboUrgence, bonLaboAssurance, bonLaboObs, dossierNotes,
    typeSuivi, suiviDepartement,
  ]);

  // Synchroniser isOnline dans un ref (évite les closures périmées dans doAutoSave)
  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  // Sauvegarde de secours : flush du brouillon si l'onglet est masqué ou la page quittée
  useEffect(() => {
    const flush = () => {
      if (consultationId && !isAutoSavingRef.current) {
        doAutoSave(consultationId);
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    const onPageHide = () => flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [consultationId, doAutoSave]);

  // --- INIT ---
  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'medecin' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    if (!patientId) { navigate('/medecin/rendez-vous', { replace: true }); return; }
    initPage();
  }, [user, role, patientId]);

  const initPage = async () => {
    setLoading(true);
    try {
      await Promise.all([loadPatient(), loadDepartements(), loadMedecinsForSuivi(), loadCustomAnalysesFromDb(), loadCatalogueForMatching()]);
      const medId = await loadMedecinInfo();
      if (medId) await loadOrCreateConsultation(medId);
    } catch (err) {
      console.error('Erreur init:', err);
    } finally {
      setLoading(false);
      setTimeout(() => { isInitialLoad.current = false; }, 500);
    }
  };

  const loadMedecinInfo = async (): Promise<string> => {
    const { data } = await supabase.from('medecins').select('*').eq('user_id', user!.id).maybeSingle();
    if (data) {
      setMedecinId(data.id);
      medecinIdRef.current = data.id;
      setMedecinInfo(data);
      return data.id;
    }
    return '';
  };

  const loadPatient = async () => {
    const { data } = await supabase.from('patients').select('*').eq('id', patientId).single();
    if (data) {
      setPatient(data);
      setDossierEditData({
        telephone: data.telephone || '',
        email: data.email || '',
        adresse: data.adresse || '',
        allergies: data.allergies || '',
        maladies_chroniques: data.maladies_chroniques || '',
        antecedents_medicaux: data.antecedents_medicaux || '',
        antecedents_chirurgicaux: data.antecedents_chirurgicaux || '',
        groupe_sanguin: data.groupe_sanguin || '',
      });
      if (user && role) {
        await logAccess({
          userId: user.id,
          userRole: role,
          actionType: 'VIEW',
          resourceType: 'CONSULTATION',
          resourceId: patientId,
          patientId: patientId,
          details: { patient_nom: `${data.prenom} ${data.nom}`, action: 'Consultation ouverte' }
        });
      }
    }
  };

  const loadMedecinsForSuivi = async () => {
    const { data } = await supabase.from('medecins').select('id, nom, prenom, specialite').not('nom', 'is', null).order('nom');
    if (data) setMedecinsList(data);
  };

  const loadDepartements = async () => {
    const { data } = await supabase.from('medecins').select('specialite').not('specialite', 'is', null);
    if (data) {
      const unique = [...new Set(data.map((d: any) => d.specialite).filter(Boolean))] as string[];
      setDepartements(unique.sort());
    }
  };

  const loadCustomAnalysesFromDb = async () => {
    try {
      const { data } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('nom')
        .eq('categorie', 'Analyses personnalisées')
        .order('nom');
      if (data && data.length > 0) {
        const items = data.map((a: any) => ({
          id: a.nom,
          label: a.nom,
        }));
        setDbCustomAnalyses([{ nom: 'Analyses personnalisées', items }]);
      }
    } catch (err) {
      console.error('Erreur chargement analyses personnalisées:', err);
    }
  };

  const loadCatalogueForMatching = async () => {
    try {
      const { data } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('id, code, nom');
      if (data) {
        setCatalogueEntries(data);
      }
    } catch (err) {
      console.error('Erreur chargement catalogue:', err);
    }
  };

  const loadOrCreateConsultation = async (medId: string) => {
    // Vérifier s'il y a d'autres consultations en cours pour ce médecin (patient différent)
    if (!skipEnCoursCheck.current) {
      const { data: autresEnCours } = await supabase
        .from('consultations')
        .select('id, patient_id, statut, rendez_vous_id, rendez_vous(statut)')
        .eq('medecin_id', medId)
        .in('statut', ['en_cours', 'partiellement_termine'])
        .neq('patient_id', patientId);

      if (autresEnCours && autresEnCours.length > 0) {
        // Auto-fermer les consultations périmées : "en_cours" dont le rendez-vous
        // n'est plus actif (terminé / annulé / manqué). Sans cela, une consultation
        // orpheline bloquait à tort la création de toute nouvelle consultation.
        const staleIds = autresEnCours
          .filter((c: any) => {
            const rdvStatut = Array.isArray(c.rendez_vous) ? c.rendez_vous[0]?.statut : c.rendez_vous?.statut;
            return c.statut === 'en_cours' && c.rendez_vous_id && rdvStatut && rdvStatut !== 'en_cours';
          })
          .map((c: any) => c.id);

        if (staleIds.length > 0) {
          try {
            await supabase
              .from('consultations')
              .update({ statut: 'termine', updated_at: new Date().toISOString() })
              .in('id', staleIds);
          } catch (_) { /* non-bloquant */ }
        }

        const stillActive = autresEnCours.filter((c: any) => !staleIds.includes(c.id));

        if (stillActive.length > 0) {
          const { data: patientEnCours } = await supabase
            .from('patients')
            .select('prenom, nom')
            .eq('id', stillActive[0].patient_id)
            .single();

          const nomPatientEnCours = patientEnCours
            ? `${patientEnCours.prenom} ${patientEnCours.nom}`
            : 'Patient inconnu';
          setConsultationEnCoursPatient(nomPatientEnCours);
          setConsultationsAFermer(stillActive.map((c) => c.id));
          setShowConsultationEnCoursModal(true);
          setLoading(false);
          return;
        }
      }
    }
    skipEnCoursCheck.current = false;

    // Nettoyer la file d'attente : remettre tout "en_cours" à "en_attente" pour éviter les doublons visuels
    try {
      await supabase
        .from('file_attente')
        .update({ statut: 'en_attente', updated_at: new Date().toISOString() })
        .eq('statut', 'en_cours');
    } catch (_) {
      // Non-bloquant : la file d'attente peut ne pas exister pour certains profils
    }

    // 1. Check if there's an existing draft or paused consultation for this patient+medecin+rdv
    let query = supabase
      .from('consultations')
      .select('*')
      .eq('patient_id', patientId)
      .in('statut', ['en_cours', 'partiellement_termine'])
      .order('created_at', { ascending: false })
      .limit(1);
    if (rdvId) query = query.eq('rendez_vous_id', rdvId);
    let { data: existing } = await query.maybeSingle();

    // Fallback: check any draft/paused for this patient by this medecin (no rdv match)
    if (!existing) {
      const { data: fallback } = await supabase
        .from('consultations')
        .select('*')
        .eq('patient_id', patientId)
        .eq('medecin_id', medId)
        .in('statut', ['en_cours', 'partiellement_termine'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      existing = fallback;
    }

    if (existing) {
      // Resume existing draft or paused consultation
      setConsultationId(existing.id);
      consultationRdvIdRef.current = existing.rendez_vous_id || null;
      setDateConsultation(existing.date_consultation || new Date().toISOString().split('T')[0]);
      if (existing.poids !== null) setPoids(String(existing.poids));
      if (existing.taille !== null) setTaille(String(existing.taille));
      if (existing.temperature !== null) setTemperature(String(existing.temperature));
      if (existing.saturation !== null) setSaturation(String(existing.saturation));
      if (existing.symptomes_signes) setSymptomes(existing.symptomes_signes);
      if (existing.antecedents_medicaux_connus) setAntecedentsConnus(existing.antecedents_medicaux_connus);
      if (existing.examen) setExamen(existing.examen);
      if (existing.diagnostic) setDiagnostic(existing.diagnostic);
      if (existing.prochain_rdv) setProchainRdv(existing.prochain_rdv);
      if (existing.suivi_vers) setSuiviVers(existing.suivi_vers);
      if (existing.mot_fin) setMotFin(existing.mot_fin);
      if (existing.patient_regulier_medicaments !== null) setPatientRegulier(existing.patient_regulier_medicaments);

      // Restore supplementary data
      applyDonneesSupplementaires(existing.donnees_supplementaires as DonneesSupplementaires | null);

      // Restore traitement_en_cours medicaments list (from supplementary or text)
      if (existing.donnees_supplementaires) {
        const supp = existing.donnees_supplementaires as DonneesSupplementaires;
        if (supp.traitementNotes !== undefined) setTraitementNotes(supp.traitementNotes);
      }

      // Marquer ce patient comme "en_cours" dans la file d'attente
      try {
        await supabase
          .from('file_attente')
          .update({ statut: 'en_cours', updated_at: new Date().toISOString() })
          .eq('patient_id', patientId);
      } catch (_) { /* non-bloquant */ }
    } else {
      // Créer un nouveau brouillon — la contrainte DB empêchera le doublon si un autre existe déjà
      const now = new Date();
      const heureDebut = now.toTimeString().slice(0, 8);
      const { data: newCons, error } = await supabase
        .from('consultations')
        .insert({
          patient_id: patientId,
          medecin_id: medId,
          rendez_vous_id: rdvId || null,
          date_consultation: new Date().toISOString().split('T')[0],
          heure_debut: heureDebut,
          statut: 'en_cours',
          donnees_supplementaires: {} as any,
        })
        .select()
        .single();

      if (error) {
        // Si l'erreur vient de la contrainte "un seul en_cours par médecin", afficher le modal
        if (error.message?.includes('déjà une consultation en cours') || error.code === 'P0001') {
          const { data: autresEnCoursNow } = await supabase
            .from('consultations')
            .select('id, patient_id')
            .eq('medecin_id', medId)
            .in('statut', ['en_cours', 'partiellement_termine'])
            .neq('patient_id', patientId);

          if (autresEnCoursNow && autresEnCoursNow.length > 0) {
            const { data: patientEnCours } = await supabase
              .from('patients')
              .select('prenom, nom')
              .eq('id', autresEnCoursNow[0].patient_id)
              .single();
            const nomPatientEnCours = patientEnCours
              ? `${patientEnCours.prenom} ${patientEnCours.nom}`
              : 'Patient inconnu';
            setConsultationEnCoursPatient(nomPatientEnCours);
            setConsultationsAFermer(autresEnCoursNow.map((c) => c.id));
            setShowConsultationEnCoursModal(true);
            setLoading(false);
            return;
          }
        }
        throw error;
      }
      if (newCons) {
        setConsultationId(newCons.id);
        consultationRdvIdRef.current = newCons.rendez_vous_id || null;
        // Marquer ce patient comme "en_cours" dans la file d'attente
        try {
          await supabase
            .from('file_attente')
            .update({ statut: 'en_cours', updated_at: new Date().toISOString() })
            .eq('patient_id', patientId);
        } catch (_) { /* non-blocant */ }
      }
    }
  };

  // --- HANDLERS MODAL CONSULTATION EN COURS ---
  const handleConfirmNouvelleConsultation = async () => {
    setShowConsultationEnCoursModal(false);
    const now = new Date();

    // Récupérer les rdv_id liés avant de fermer
    const { data: consToClose } = await supabase
      .from('consultations')
      .select('id, rendez_vous_id, patient_id')
      .in('id', consultationsAFermer);

    await supabase
      .from('consultations')
      .update({ statut: 'termine', heure_fin: now.toTimeString().slice(0, 8), updated_at: now.toISOString() })
      .in('id', consultationsAFermer);

    // Nettoyer la file d'attente pour les patients des consultations fermées
    if (consToClose) {
      const patientIds = consToClose.map((c: any) => c.patient_id).filter(Boolean);
      try {
        await supabase
          .from('file_attente')
          .update({ statut: 'en_attente', updated_at: now.toISOString() })
          .in('patient_id', patientIds)
          .eq('statut', 'en_cours');
      } catch (_) { /* non-bloquant */ }
    }

    // Fermer aussi les rendez-vous liés pour éviter l'incohérence
    if (consToClose) {
      const rdvIds = consToClose
        .map((c: any) => c.rendez_vous_id)
        .filter(Boolean);
      if (rdvIds.length > 0) {
        await supabase
          .from('rendez_vous')
          .update({ statut: 'termine', heure_fin_reel: now.toTimeString().slice(0, 8) })
          .in('id', rdvIds);
      }
    }

    skipEnCoursCheck.current = true;
    setLoading(true);
    try {
      await loadOrCreateConsultation(medecinIdRef.current);
    } finally {
      setLoading(false);
      setTimeout(() => { isInitialLoad.current = false; }, 500);
    }
  };

  const handleCancelNouvelleConsultation = () => {
    setShowConsultationEnCoursModal(false);
    navigate('/medecin/rendez-vous', { replace: true });
  };

  // --- SCROLL TRACKING ---
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 200;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const ref = sectionRefs[SECTIONS[i].key];
        if (ref.current && ref.current.offsetTop <= scrollY) {
          setActiveSection(SECTIONS[i].key);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (key: SectionKey) => {
    sectionRefs[key].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // --- HELPERS ---
  const addMedicamentEnCours = () => setMedicamentsEnCours((prev) => [...prev, { id: Date.now().toString(), nom: '', dosage: '' }]);
  const updateMedicamentEnCours = (id: string, field: 'nom' | 'dosage', v: string) => {
    setMedicamentsEnCours((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: v } : m)));
  };
  const removeMedicamentEnCours = (id: string) => {
    if (medicamentsEnCours.length <= 1) return;
    setMedicamentsEnCours((prev) => prev.filter((m) => m.id !== id));
  };

  const addMedicamentPrescrit = () => setMedicamentsPrescrits((prev) => [...prev, { id: Date.now().toString(), nom: '', dosageNb: '1', forme: 'comprime', matin: true, midi: false, soir: false, nbBoites: '1', conditionnement: getDefaultConditionnement('comprime'), instructions: '', duree: '' }]);
  const updateMedicamentPrescrit = (id: string, field: 'nom' | 'dosageNb' | 'nbBoites' | 'conditionnement' | 'forme' | 'instructions' | 'duree' | 'voieInjection' | 'siteApplication' | 'siteGouttes' | 'dissolution', v: string) => {
    setMedicamentsPrescrits((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const updated = { ...m, [field]: v };
      // Quand la forme change, recalculer le conditionnement par défaut
      if (field === 'forme') {
        updated.conditionnement = getDefaultConditionnement(v);
      }
      return updated;
    }));
  };
  const toggleMoment = (id: string, moment: 'matin' | 'midi' | 'soir') => {
    setMedicamentsPrescrits((prev) => prev.map((m) => (m.id === id ? { ...m, [moment]: !m[moment] } : m)));
  };
  const removeMedicamentPrescrit = (id: string) => {
    if (medicamentsPrescrits.length <= 1) return;
    setMedicamentsPrescrits((prev) => prev.filter((m) => m.id !== id));
  };

  const addSoin = () => setSoins((prev) => [...prev, { id: Date.now().toString(), date: dateConsultation, description: '' }]);
  const updateSoin = (id: string, field: 'date' | 'description', v: string) => {
    setSoins((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: v } : s)));
  };
  const removeSoin = (id: string) => {
    if (soins.length <= 1) return;
    setSoins((prev) => prev.filter((s) => s.id !== id));
  };

  // --- DOSSIER EDIT ---
  const startDossierEdit = () => {
    if (!patient) return;
    setDossierEditData({
      telephone: patient.telephone || '',
      email: patient.email || '',
      adresse: patient.adresse || '',
      allergies: patient.allergies || '',
      maladies_chroniques: patient.maladies_chroniques || '',
      antecedents_medicaux: patient.antecedents_medicaux || '',
      antecedents_chirurgicaux: patient.antecedents_chirurgicaux || '',
      groupe_sanguin: patient.groupe_sanguin || '',
    });
    setEditDossierMode(true);
  };

  const cancelDossierEdit = () => {
    setEditDossierMode(false);
    setDossierEditSaving(false);
  };

  const saveDossierEdit = async () => {
    if (!patient) return;
    setDossierEditSaving(true);
    try {
      await offlineMutate({
        table: 'patients',
        operation: 'UPDATE',
        recordId: patient.id,
        data: {
          telephone: dossierEditData.telephone || null,
          email: dossierEditData.email || null,
          adresse: dossierEditData.adresse || null,
          allergies: dossierEditData.allergies || null,
          maladies_chroniques: dossierEditData.maladies_chroniques || null,
          antecedents_medicaux: dossierEditData.antecedents_medicaux || null,
          antecedents_chirurgicaux: dossierEditData.antecedents_chirurgicaux || null,
          groupe_sanguin: dossierEditData.groupe_sanguin || null,
        },
        expectedUpdatedAt: (patient as any).updated_at || null,
        metadata: { summary: `MàJ dossier — ${patient.prenom} ${patient.nom}` },
        onlineExecutor: async () => {
          await supabase.from('patients').update({
            telephone: dossierEditData.telephone || null,
            email: dossierEditData.email || null,
            adresse: dossierEditData.adresse || null,
            allergies: dossierEditData.allergies || null,
            maladies_chroniques: dossierEditData.maladies_chroniques || null,
            antecedents_medicaux: dossierEditData.antecedents_medicaux || null,
            antecedents_chirurgicaux: dossierEditData.antecedents_chirurgicaux || null,
            groupe_sanguin: dossierEditData.groupe_sanguin || null,
          }).eq('id', patient.id);
          return { id: patient.id };
        },
      });
      // Reload patient (try online, skip if offline)
      try { await loadPatient(); } catch (_) { /* offline */ }
      setEditDossierMode(false);
      setSaveStatus({ type: 'success', msg: 'Dossier patient mis à jour !' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch {
      setSaveStatus({ type: 'error', msg: 'Erreur lors de la mise à jour du dossier.' });
    } finally {
      setDossierEditSaving(false);
    }
  };

  // --- BON LABO HELPERS ---
  const addCustomAnalyse = async (preset?: { id: string; code: string; nom: string }) => {
    const label = preset ? preset.nom : newCustomAnalyse.trim();
    if (!label) return;

    const customId = `custom-${Date.now()}`;
    let catalogueId = preset?.id;

    if (!catalogueId) {
      try {
        const { ids, catalogue: updatedCatalogue } = await resolveAnalysesToCatalogueIds(
          [label],
          catalogueEntries,
          'Analyses personnalisées',
        );
        catalogueId = ids[0];
        if (updatedCatalogue.length > catalogueEntries.length) {
          setCatalogueEntries(updatedCatalogue);
        }
      } catch (err) {
        console.error('Erreur résolution analyse:', err);
      }
    }

    setCustomAnalyses((prev) => [...prev, { id: customId, label, catalogueId }]);
    setSelectedAnalyses((prev) => [...prev, customId]);
    setNewCustomAnalyse('');
    setShowAnalyseSuggestions(false);
  };

  const selectAnalyseSuggestion = (entry: { id: string; code: string; nom: string }) => {
    addCustomAnalyse(entry);
  };

  const toggleAnalyse = (id: string) => {
    setSelectedAnalyses((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const removeCustomAnalyse = (id: string) => {
    setCustomAnalyses((prev) => prev.filter((a) => a.id !== id));
    setSelectedAnalyses((prev) => prev.filter((a) => a !== id));
  };

  const analyseSuggestions = useMemo(() => {
    const term = newCustomAnalyse.trim().toLowerCase();
    if (!term) return [];
    return catalogueEntries
      .filter((e) => {
        if (customAnalyses.some((c) => c.catalogueId === e.id)) return false;
        const nom = (e.nom || '').toLowerCase();
        const code = (e.code || '').toLowerCase();
        return nom.includes(term) || code.includes(term);
      })
      .slice(0, 8);
  }, [newCustomAnalyse, catalogueEntries, customAnalyses]);

  // --- TERMINER CONSULTATION ---
  const handleTerminerConsultation = async () => {
    if (!patient || !medecinId || !consultationId) return;
    if (!diagnostic.trim()) {
      setSaveStatus({ type: 'error', msg: 'Le diagnostic est obligatoire pour terminer la consultation.' });
      scrollToSection('diagnostic');
      return;
    }
    setSaving(true);
    setSaveStatus(null);

    try {
      const now = new Date();
      const heureFin = now.toTimeString().slice(0, 8);

      // Build suivi string
      const suiviFinal = typeSuivi === 'departement'
        ? `Département: ${suiviDepartement}`
        : (suiviVers.trim() || null);

      // 1. Final update de la consultation — DIRECT (pas offlineMutate, la terminaison doit être immédiate)
      const finalSupp = buildDonneesSupplementaires();
      const { error: updateError } = await supabase.from('consultations').update({
        date_consultation: dateConsultation,
        heure_fin: heureFin,
        poids: poids ? parseFloat(poids) : null,
        taille: taille ? parseFloat(taille) : null,
        temperature: temperature ? parseFloat(temperature) : null,
        tension: buildTensionSummary(),
        saturation: saturation ? parseInt(saturation) : null,
        symptomes_signes: symptomes.trim() || null,
        traitement_en_cours: [
          medicamentsEnCours.filter((m) => m.nom.trim()).map((m) => `${m.nom}${m.dosage ? ` (${m.dosage})` : ''}`).join('\n'),
          traitementNotes || null
        ].filter(Boolean).join('\n\n') || null,
        patient_regulier_medicaments: patientRegulier,
        antecedents_medicaux_connus: antecedentsConnus || null,
        examen: examen || null,
        diagnostic: diagnostic.trim(),
        prochain_rdv: prochainRdv || null,
        suivi_vers: suiviFinal,
        mot_fin: motFin || null,
        statut: 'termine',
        donnees_supplementaires: finalSupp as any,
        updated_at: new Date().toISOString(),
      }).eq('id', consultationId);

      if (updateError) throw updateError;

      // Secondary operations wrapped in try/catch — non-blocking
      try {
        await supabase
          .from('file_attente')
          .update({ statut: 'en_attente', updated_at: new Date().toISOString() })
          .eq('patient_id', patientId)
          .eq('statut', 'en_cours');
      } catch (_) { /* non-bloquant */ }

      // 2. Notes du dossier patient
      try {
        if (dossierNotes !== patient?.notes_medicales) {
          await supabase.from('patients').update({ notes_medicales: dossierNotes }).eq('id', patientId);
        }
      } catch (_) { /* non-bloquant */ }

      // 3. Bon de laboratoire (avec analyses cochées)
      try {
        const allAnalysesIds = selectedAnalyses.filter((id) => !id.startsWith('custom-'));
        const customIds = selectedAnalyses.filter((id) => id.startsWith('custom-'));
        const allAnalysesLabels = [
          ...allAnalysesIds.map((id) => {
            const item = CATEGORIES_ANALYSES.flatMap((c) => c.items).find((i) => i.id === id);
            return item ? item.label : id;
          }),
          ...customIds.map((id) => {
            const custom = customAnalyses.find((a) => a.id === id);
            return custom ? custom.label : id;
          }),
        ];
        if (showBonLabo && allAnalysesLabels.length > 0) {
          const matchedIds = (await resolveAnalysesToCatalogueIds(allAnalysesLabels, catalogueEntries)).ids;
          await supabase.from('bons_laboratoire').insert({
            patient_id: patientId,
            medecin_id: medecinId,
            consultation_id: consultationId || null,
            date_prescription: dateConsultation,
            type_analyse: allAnalysesLabels.join(' ; '),
            description: null,
            observations_cliniques: bonLaboObs || null,
            urgences: bonLaboUrgence,
            statut: 'en_attente',
            analyses_ids: matchedIds.length > 0 ? matchedIds : null,
          });
        }
      } catch (_) { /* non-bloquant */ }

      // 4. Prescription
      try {
        const medicamentsPrescritsValides = medicamentsPrescrits.filter((m) => m.nom.trim());
        const medicamentsNoms = medicamentsPrescritsValides.map((m) => formatLigneOrdonnance(m));
        const instructionsCombinees = medicamentsPrescritsValides
          .map((m) => m.instructions.trim())
          .filter(Boolean)
          .join('\n');
        if (medicamentsNoms.length > 0) {
          const dureesCombinees = medicamentsPrescritsValides
            .map((m) => m.duree.trim())
            .filter(Boolean)
            .join(' ; ');
          await supabase.from('prescriptions').insert({
            patient_id: patientId,
            medecin_id: medecinId,
            date_prescription: dateConsultation,
            diagnostic: diagnostic.trim(),
            medicaments: medicamentsNoms,
            instructions: instructionsCombinees || null,
            duree_traitement: dureesCombinees || null,
          });
        }
      } catch (_) { /* non-bloquant */ }

      // 5. Fiche de soin + fiche de surveillance
      try {
        const soinsValides = soins.filter((s) => s.description.trim());
        if (showFicheSoin && soinsValides.length > 0) {
          for (const soin of soinsValides) {
            const { data: ficheData } = await supabase
              .from('fiches_soins')
              .insert({
                patient_id: patientId,
                medecin_id: medecinId,
                consultation_id: consultationId,
                date_soin: soin.date || dateConsultation,
                soins_a_administrer: soin.description.trim(),
                statut: 'en_cours',
              })
              .select()
              .single();

            if (ficheData) {
              await supabase.from('fiches_surveillance').insert({
                fiche_soin_id: ficheData.id,
                patient_id: patientId,
                medecin_traitant_id: medecinId,
              });
            }
          }
        }
      } catch (_) { /* non-bloquant */ }

      // 6. Mettre à jour le RDV
      try {
        const medicamentsText = medicamentsPrescrits.filter((m) => m.nom.trim()).map((m) => formatLigneOrdonnance(m)).join('\n');
        const traitementText = [
          soins.filter((s) => s.description.trim()).length > 0 ? `Soins:\n${soins.filter((s) => s.description.trim()).map((s) => `${s.date || dateConsultation}: ${s.description}`).join('\n')}` : null,
          medicamentsPrescrits.filter((m) => m.nom.trim()).length > 0 ? `Médicaments:\n${medicamentsPrescrits.filter((m) => m.nom.trim()).map((m) => formatLigneOrdonnance(m)).join('\n')}` : null,
        ].filter(Boolean).join('\n\n');
        let rdvToClose = rdvId || consultationRdvIdRef.current;
        if (!rdvToClose) {
          // Filet de sécurité : retrouver le rendez-vous lié à cette consultation
          const { data: consRdvRow } = await supabase
            .from('consultations')
            .select('rendez_vous_id')
            .eq('id', consultationId)
            .maybeSingle();
          rdvToClose = consRdvRow?.rendez_vous_id || null;
        }
        if (rdvToClose) {
          await supabase.from('rendez_vous').update({
            statut: 'termine',
            heure_fin_reel: heureFin,
            diagnostique: diagnostic.trim(),
            medicaments: medicamentsText || null,
            traitement: traitementText || null,
            prochain_rdv: prochainRdv || null,
            notes_medecin: motFin || null,
          }).eq('id', rdvToClose);
        }
      } catch (e) {
        console.error('Erreur mise à jour du rendez-vous:', e);
      }

      setSaveStatus({ type: 'success', msg: 'Consultation terminée avec succès !' });
      setSaving(false);
      navigate('/medecin/rendez-vous', { replace: true });
    } catch (err: any) {
      console.error('Erreur terminaison consultation:', err);
      setSaveStatus({ type: 'error', msg: err.message || 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  // --- RENDER ---
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Préparation de la consultation...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500"></i>
          <p className="mt-4 text-xl font-semibold">Patient introuvable</p>
          <button onClick={() => navigate('/medecin/rendez-vous')} className="mt-4 px-6 py-2 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark cursor-pointer whitespace-nowrap">
            Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  const age = new Date().getFullYear() - new Date(patient.date_naissance).getFullYear();
  const isGynecologue = medecinInfo?.specialite?.toLowerCase().includes('gynéco');
  const showDossierRose = isGynecologue && patient.sexe === 'F';
  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors text-sm bg-white";
  const textareaClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors resize-none text-sm bg-white";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Top patient bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/medecin/rendez-vous')}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {patient.prenom} {patient.nom}
                </h1>
                <p className="text-sm text-gray-500">
                  {age} ans • {patient.sexe === 'M' ? 'Masculin' : patient.sexe === 'F' ? 'Féminin' : 'Sexe non renseigné'} • Dossier {patient.numero_dossier || 'N/A'} • {patient.groupe_sanguin || 'GS inconnu'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Dossier Rose button - only for gynecologue with female patient */}
              {showDossierRose && (
                <button
                  onClick={() => setDossierRoseMode(!dossierRoseMode)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all flex items-center gap-2 ${
                    dossierRoseMode
                      ? 'bg-rose-600 text-white shadow-sm'
                      : 'bg-rose-50 text-rose-600 border-2 border-rose-200 hover:bg-rose-100 hover:border-rose-300'
                  }`}
                >
                  <i className="ri-heart-pulse-line w-4 h-4 flex items-center justify-center"></i>
                  {dossierRoseMode ? 'Fermer Dossier Rose' : 'Dossier Rose'}
                </button>
              )}
              {/* Auto-save indicator */}
              <div className="hidden sm:flex items-center gap-1.5 text-xs">
                {autoSaveStatus === 'saving' && (
                  <span className="text-amber-600 flex items-center gap-1">
                    <i className="ri-loader-4-line animate-spin w-3.5 h-3.5 flex items-center justify-center"></i>
                    Enregistrement...
                  </span>
                )}
                {autoSaveStatus === 'saved' && (
                  <span className="text-green-600 flex items-center gap-1">
                    <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    Brouillon sauvegardé
                  </span>
                )}
                {autoSaveStatus === 'error' && (
                  <span className="text-red-600 flex items-center gap-1">
                    <i className="ri-error-warning-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    Erreur sauvegarde
                  </span>
                )}
                {autoSaveStatus === 'idle' && consultationId && (
                  <span className="text-gray-400 flex items-center gap-1">
                    <i className="ri-check-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    Prêt
                  </span>
                )}
              </div>
              <button
                onClick={handleTerminerConsultation}
                disabled={saving}
                className="px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                {saving ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Sauvegarde...</>
                ) : (
                  <><i className="ri-check-line"></i>Terminer la consultation</>
                )}
              </button>
            </div>
          </div>

          {saveStatus && (
            <div className={`mt-3 p-3 rounded-xl text-sm flex items-center gap-2 ${
              saveStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              <i className={saveStatus.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
              {saveStatus.msg}
            </div>
          )}

          {/* Horizontal section tabs (mobile) */}
          <div className="flex gap-1 mt-3 overflow-x-auto pb-1 lg:hidden">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => scrollToSection(s.key)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  activeSection === s.key
                    ? 'bg-clinic-blue text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Left sidebar navigation (desktop) */}
          <nav className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28 space-y-1">
              {SECTIONS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => scrollToSection(s.key)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeSection === s.key
                      ? 'bg-clinic-blue text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                                    {s.label}
                </button>
              ))}
            </div>
          </nav>

          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-8">
            {/* ===== MAIN CONTENT ===== */}
            {dossierRoseMode ? (
              /* DOSSIER ROSE - Plein écran */
              <div className="bg-white rounded-2xl border-2 border-rose-200 overflow-hidden">
                <div className="p-6 border-b border-rose-100 bg-rose-50/30 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <i className="ri-heart-pulse-line text-rose-500 w-5 h-5 flex items-center justify-center"></i>
                    Dossier Rose — {patient.prenom} {patient.nom}
                  </h2>
                  <button
                    onClick={() => setDossierRoseMode(false)}
                    className="px-4 py-2 bg-white border-2 border-rose-200 text-rose-600 rounded-xl hover:bg-rose-50 text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-colors"
                  >
                    <i className="ri-arrow-go-back-line"></i>
                    Retour au dossier général
                  </button>
                </div>
                <div className="p-6">
                  <DossierRoseForm
                    patientId={patientId}
                    userId={user!.id}
                    onSaved={() => {}}
                  />
                </div>
              </div>
            ) : (
              <>
            {/* ===== SECTION 1: DOSSIER GÉNÉRAL + ASSURANCE ===== */}
            <div ref={sectionRefs.dossier} id="section-dossier" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Dossier Général
                </h2>
                {!editDossierMode ? (
                  <button
                    onClick={startDossierEdit}
                    className="px-4 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-colors"
                  >
                    <i className="ri-edit-line"></i>Modifier le dossier
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={cancelDossierEdit} className="px-4 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm font-semibold cursor-pointer whitespace-nowrap">Annuler</button>
                    <button
                      onClick={saveDossierEdit}
                      disabled={dossierEditSaving}
                      className="px-4 py-1.5 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {dossierEditSaving ? <><i className="ri-loader-4-line animate-spin"></i>Sauvegarde...</> : <><i className="ri-save-line"></i>Enregistrer</>}
                    </button>
                  </div>
                )}
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Nom</p>
                    <p className="text-sm font-semibold text-gray-900">{patient.nom}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Prénom</p>
                    <p className="text-sm font-semibold text-gray-900">{patient.prenom}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Date de naissance</p>
                    <p className="text-sm font-semibold text-gray-900">{new Date(patient.date_naissance).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Téléphone</p>
                    {editDossierMode ? (
                      <input type="text" value={dossierEditData.telephone} onChange={(e) => setDossierEditData((prev) => ({ ...prev, telephone: e.target.value }))} className={inputClass} placeholder="N° de téléphone" />
                    ) : (
                      <p className="text-sm font-semibold text-gray-900">{patient.telephone || '-'}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    {editDossierMode ? (
                      <input type="email" value={dossierEditData.email} onChange={(e) => setDossierEditData((prev) => ({ ...prev, email: e.target.value }))} className={inputClass} placeholder="Email" />
                    ) : (
                      <p className="text-sm font-semibold text-gray-900 truncate">{patient.email || '-'}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Groupe sanguin</p>
                    {editDossierMode ? (
                      <select value={dossierEditData.groupe_sanguin} onChange={(e) => setDossierEditData((prev) => ({ ...prev, groupe_sanguin: e.target.value }))} className={inputClass}>
                        <option value="">Non renseigné</option>
                        <option value="A+">A+</option><option value="A-">A-</option>
                        <option value="B+">B+</option><option value="B-">B-</option>
                        <option value="AB+">AB+</option><option value="AB-">AB-</option>
                        <option value="O+">O+</option><option value="O-">O-</option>
                      </select>
                    ) : (
                      <p className="text-sm font-semibold text-gray-900">{patient.groupe_sanguin || '-'}</p>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <p className="text-xs text-gray-500 mb-1">Adresse</p>
                    {editDossierMode ? (
                      <input type="text" value={dossierEditData.adresse} onChange={(e) => setDossierEditData((prev) => ({ ...prev, adresse: e.target.value }))} className={inputClass} placeholder="Adresse" />
                    ) : (
                      <p className="text-sm font-semibold text-gray-900">{patient.adresse || '-'}</p>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <p className="text-xs text-gray-500 mb-1">Allergies connues</p>
                    {editDossierMode ? (
                      <textarea value={dossierEditData.allergies} onChange={(e) => setDossierEditData((prev) => ({ ...prev, allergies: e.target.value }))} className={textareaClass} rows={2} maxLength={500} placeholder="Allergies..." />
                    ) : (
                      <p className="text-sm text-red-600 font-semibold">{patient.allergies || 'Aucune'}</p>
                    )}
                  </div>
                </div>

                {/* Antécédents éditables */}
                {editDossierMode && (
                  <div className="border-t border-gray-100 pt-6 space-y-4">
                    <h3 className="text-md font-bold text-gray-800">Antécédents médicaux</h3>
                    <div>
                      <label className={labelClass}>Maladies chroniques</label>
                      <textarea value={dossierEditData.maladies_chroniques} onChange={(e) => setDossierEditData((prev) => ({ ...prev, maladies_chroniques: e.target.value }))} className={textareaClass} rows={2} maxLength={500} />
                    </div>
                    <div>
                      <label className={labelClass}>Antécédents médicaux</label>
                      <textarea value={dossierEditData.antecedents_medicaux} onChange={(e) => setDossierEditData((prev) => ({ ...prev, antecedents_medicaux: e.target.value }))} className={textareaClass} rows={2} maxLength={500} />
                    </div>
                    <div>
                      <label className={labelClass}>Antécédents chirurgicaux</label>
                      <textarea value={dossierEditData.antecedents_chirurgicaux} onChange={(e) => setDossierEditData((prev) => ({ ...prev, antecedents_chirurgicaux: e.target.value }))} className={textareaClass} rows={2} maxLength={500} />
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-6">
                  <label className={labelClass}>Notes du dossier</label>
                  <textarea
                    value={dossierNotes}
                    onChange={(e) => setDossierNotes(e.target.value)}
                    className={textareaClass}
                    rows={3}
                    placeholder="Notes générales sur le patient..."
                    maxLength={500}
                  />
                </div>
              </div>
            </div>

            {/* ===== SECTION 2: CONSULTATION ===== */}
            <div ref={sectionRefs.consultation} id="section-consultation" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Consultation
                </h2>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className={labelClass}>Date de consultation</label>
                  <input type="date" value={dateConsultation} readOnly className={inputClass + ' max-w-xs bg-gray-100 cursor-not-allowed'} />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div>
                    <label className={labelClass}>Poids (kg)</label>
                    <input type="number" step="0.1" min="0" value={poids} onChange={(e) => setPoids(e.target.value)} className={inputClass} placeholder="ex: 70.5" />
                  </div>
                  <div>
                    <label className={labelClass}>Taille (cm)</label>
                    <input type="number" step="0.1" min="0" value={taille} onChange={(e) => setTaille(e.target.value)} className={inputClass} placeholder="ex: 175" />
                  </div>
                  <div>
                    <label className={labelClass}>Température (°C)</label>
                    <input type="number" step="0.1" min="0" value={temperature} onChange={(e) => setTemperature(e.target.value)} className={inputClass} placeholder="ex: 37.5" />
                  </div>
                  <div className="col-span-2 sm:col-span-3 lg:col-span-5">
                    <label className={labelClass}>Tension</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Bras gauche</label>
                        <input type="text" value={tensionValues.bras_gauche} onChange={(e) => setTensionValues((prev) => ({ ...prev, bras_gauche: e.target.value }))} className={inputClass} placeholder="ex: 12/8" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Bras droit</label>
                        <input type="text" value={tensionValues.bras_droit} onChange={(e) => setTensionValues((prev) => ({ ...prev, bras_droit: e.target.value }))} className={inputClass} placeholder="ex: 12/8" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Debout</label>
                        <input type="text" value={tensionValues.debout} onChange={(e) => setTensionValues((prev) => ({ ...prev, debout: e.target.value }))} className={inputClass} placeholder="ex: 12/8" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Assis</label>
                        <input type="text" value={tensionValues.assis} onChange={(e) => setTensionValues((prev) => ({ ...prev, assis: e.target.value }))} className={inputClass} placeholder="ex: 12/8" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Couché</label>
                        <input type="text" value={tensionValues.coucher} onChange={(e) => setTensionValues((prev) => ({ ...prev, coucher: e.target.value }))} className={inputClass} placeholder="ex: 12/8" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Saturation (%)</label>
                    <input type="number" min="0" max="100" value={saturation} onChange={(e) => setSaturation(e.target.value)} className={inputClass} placeholder="ex: 98" />
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <label className="text-sm font-semibold text-gray-700 block mb-2">Symptômes et/ou Signes</label>
                  <textarea
                    value={symptomes}
                    onChange={(e) => setSymptomes(e.target.value)}
                    className={textareaClass}
                    rows={4}
                    placeholder="Décrire les symptômes et signes, ainsi que leur mode d'apparition (depuis quand, comment ils sont survenus, leur évolution...)"
                    maxLength={1000}
                  />
                </div>
              </div>
            </div>

            {/* ===== SECTION 3: ANTÉCÉDENTS ===== */}
            <div ref={sectionRefs.antecedents} id="section-antecedents" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Antécédents médicaux connus
                </h2>
                <button
                  onClick={async () => {
                    if (!patient) return;
                    setDossierEditSaving(true);
                    try {
                      await supabase.from('patients').update({
                        maladies_chroniques: dossierEditData.maladies_chroniques || null,
                        antecedents_chirurgicaux: dossierEditData.antecedents_chirurgicaux || null,
                      }).eq('id', patient.id);
                      await loadPatient();
                      setSaveStatus({ type: 'success', msg: 'Antécédents mis à jour !' });
                      setTimeout(() => setSaveStatus(null), 3000);
                    } catch {
                      setSaveStatus({ type: 'error', msg: 'Erreur lors de la sauvegarde.' });
                    } finally {
                      setDossierEditSaving(false);
                    }
                  }}
                  disabled={dossierEditSaving}
                  className="px-4 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-colors disabled:opacity-60"
                >
                  {dossierEditSaving ? <><i className="ri-loader-4-line animate-spin"></i>Sauvegarde...</> : <><i className="ri-save-line"></i>Enregistrer</>}
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Maladies chroniques (dossier)</label>
                    <textarea
                      value={dossierEditData.maladies_chroniques}
                      onChange={(e) => setDossierEditData((prev) => ({ ...prev, maladies_chroniques: e.target.value }))}
                      className={textareaClass}
                      rows={3}
                      placeholder="Ex: Diabète, HTA, Asthme..."
                      maxLength={500}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Antécédents chirurgicaux (dossier)</label>
                    <textarea
                      value={dossierEditData.antecedents_chirurgicaux}
                      onChange={(e) => setDossierEditData((prev) => ({ ...prev, antecedents_chirurgicaux: e.target.value }))}
                      className={textareaClass}
                      rows={3}
                      placeholder="Ex: Appendicectomie en 2010..."
                      maxLength={500}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>
                    Antécédents médicaux connus <span className="text-gray-400 font-normal">(ex: diabétique, HTA, asthme...)</span>
                  </label>
                  <textarea
                    value={antecedentsConnus}
                    onChange={(e) => setAntecedentsConnus(e.target.value)}
                    className={textareaClass}
                    rows={4}
                    placeholder="Lister les antécédents médicaux connus du patient..."
                    maxLength={1000}
                  />
                </div>
              </div>
            </div>

            {/* ===== SECTION 4: TRAITEMENT EN COURS ===== */}
            <div ref={sectionRefs.traitement} id="section-traitement" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Traitement en cours / Médicaments
                </h2>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className={labelClass}>Médicaments en cours</label>
                    <button onClick={addMedicamentEnCours} type="button" className="px-3 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-xs font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1">
                      <i className="ri-add-line"></i>Ajouter
                    </button>
                  </div>
                  <div className="space-y-2">
                    {medicamentsEnCours.map((m) => (
                      <div key={m.id} className="flex gap-2 items-start">
                        <div className="flex-1">
                          <MedicamentAutocomplete
                            value={m.nom}
                            onChange={(nom) => updateMedicamentEnCours(m.id, 'nom', nom)}
                            placeholder="Nom du médicament..."
                            className={inputClass}
                          />
                        </div>
                        <div className="w-32 flex-shrink-0">
                          <input
                            type="text"
                            value={m.dosage}
                            onChange={(e) => updateMedicamentEnCours(m.id, 'dosage', e.target.value)}
                            className={inputClass}
                            placeholder="Dosage"
                          />
                        </div>
                        {medicamentsEnCours.length > 1 && (
                          <button onClick={() => removeMedicamentEnCours(m.id)} type="button" className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0 mt-1">
                            <i className="ri-close-line"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={patientRegulier}
                    onChange={(e) => setPatientRegulier(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Le patient est régulier dans la prise de ses médicaments / traitement</span>
                </div>

                <div>
                  <label className={labelClass}>Notes sur le traitement</label>
                  <textarea
                    value={traitementNotes}
                    onChange={(e) => setTraitementNotes(e.target.value)}
                    className={textareaClass}
                    rows={2}
                    placeholder="Observations sur le traitement en cours..."
                    maxLength={500}
                  />
                </div>
              </div>
            </div>

            {/* ===== SECTION 5: EXAMEN ===== */}
            <div ref={sectionRefs.examen} id="section-examen" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Examen
                </h2>
              </div>
              <div className="p-6">
                <label className={labelClass}>Constat de l'examen physique</label>
                <textarea
                  value={examen}
                  onChange={(e) => setExamen(e.target.value)}
                  className={textareaClass}
                  rows={4}
                  placeholder="Décrire les constats de l'examen manuel (ex: yeux enflés, douleur à la palpation, etc.)..."
                  maxLength={1000}
                />
              </div>
            </div>

            {/* ===== SECTION 6: DIAGNOSTIC ===== */}
            <div ref={sectionRefs.diagnostic} id="section-diagnostic" className="bg-white rounded-2xl border-2 border-red-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-red-100 bg-red-50/30">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Diagnostic <span className="text-red-500 text-sm font-normal">*</span>
                </h2>
              </div>
              <div className="p-6">
                <label className={labelClass}>Diagnostic du médecin <span className="text-red-500">*</span></label>
                <textarea
                  value={diagnostic}
                  onChange={(e) => setDiagnostic(e.target.value)}
                  className={textareaClass + ' border-red-200'}
                  rows={4}
                  placeholder="Poser votre diagnostic..."
                  maxLength={1000}
                />
              </div>
            </div>

            {/* ===== SECTION 8: BON DE LABORATOIRE ===== */}
            <div ref={sectionRefs['bon-labo']} id="section-bon-labo" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Bon de Laboratoire
                </h2>
                {!bonLaboSent && (
                  <button
                    onClick={() => { setShowBonLabo(!showBonLabo); }}
                    className={`px-4 py-1.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                      showBonLabo ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-clinic-blue text-white hover:bg-clinic-blue-dark'
                    }`}
                  >
                    {showBonLabo ? 'Retirer' : 'Ajouter un bon'}
                  </button>
                )}
              </div>

              {/* Édition du bon (avant envoi au labo) */}
              {!bonLaboSent && showBonLabo && (
                <div className="p-6 space-y-5">
                  {/* Analyses checklist */}
                  <div>
                    <label className={labelClass}>Analyses demandées <span className="text-red-500">*</span></label>
                    <AnalyseChecklist
                      selectedIds={selectedAnalyses}
                      onToggle={toggleAnalyse}
                      accentColor="blue"
                      extraCategories={dbCustomAnalyses}
                    />
                  </div>

                  {/* Analyses personnalisées */}
                  {customAnalyses.length > 0 && (
                    <div className="border-t border-gray-100 pt-4">
                      <label className="text-sm font-semibold text-gray-700 mb-2 block">Analyses ajoutées manuellement</label>
                      <div className="space-y-2">
                        {customAnalyses.map((a) => (
                          <div key={a.id} className="flex items-center gap-2">
                            <label className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors flex-1 ${
                              selectedAnalyses.includes(a.id) ? 'bg-clinic-blue/10 border border-clinic-blue/30' : 'hover:bg-gray-50 border border-gray-200'
                            }`}>
                              <input
                                type="checkbox"
                                checked={selectedAnalyses.includes(a.id)}
                                onChange={() => toggleAnalyse(a.id)}
                                className="w-4 h-4 rounded border-gray-300 text-clinic-blue focus:ring-clinic-blue cursor-pointer"
                              />
                              <span className="text-sm text-gray-900">{a.label}</span>
                            </label>
                            <button
                              onClick={() => removeCustomAnalyse(a.id)}
                              type="button"
                              className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ajouter une analyse (avec suggestions du catalogue labo) */}
                  <div className="flex gap-2 items-start">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={newCustomAnalyse}
                        onChange={(e) => { setNewCustomAnalyse(e.target.value); setShowAnalyseSuggestions(true); }}
                        onFocus={() => setShowAnalyseSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowAnalyseSuggestions(false), 150)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomAnalyse(); } }}
                        className={inputClass}
                        placeholder="Rechercher ou ajouter une analyse non listée..."
                      />
                      {showAnalyseSuggestions && analyseSuggestions.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                          <p className="px-3 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                            Analyses existantes au laboratoire
                          </p>
                          {analyseSuggestions.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => selectAnalyseSuggestion(s)}
                              className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-clinic-blue/10 text-left cursor-pointer transition-colors"
                            >
                              <span className="text-sm text-gray-800 truncate">{s.nom}</span>
                              {s.code && <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{s.code}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => addCustomAnalyse()}
                      type="button"
                      className="px-4 py-2.5 bg-clinic-blue/10 text-clinic-blue rounded-xl hover:bg-clinic-blue/15 font-semibold text-sm cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
                    >
                      <i className="ri-add-line mr-1"></i>Ajouter
                    </button>
                  </div>
                  {newCustomAnalyse.trim() && analyseSuggestions.length === 0 && (
                    <p className="text-xs text-gray-400 -mt-1">
                      Aucune analyse existante ne correspond. Cliquez sur "Ajouter" pour la créer dans la catégorie "Analyse générale" (le laboratoire la classera ensuite).
                    </p>
                  )}

                  <div className="flex items-center gap-6 pt-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={bonLaboUrgence}
                        onChange={(e) => setBonLaboUrgence(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-400 cursor-pointer"
                      />
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className={`text-sm font-semibold ${bonLaboUrgence ? 'text-red-600' : 'text-gray-700'}`}>
                          <i className={`${bonLaboUrgence ? 'ri-alert-fill' : 'ri-alert-line'} mr-1`}></i>Urgent
                        </span>
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={bonLaboAssurance}
                        onChange={(e) => setBonLaboAssurance(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-400 cursor-pointer"
                      />
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className={`text-sm font-semibold ${bonLaboAssurance ? 'text-blue-600' : 'text-gray-700'}`}>
                          <i className={`${bonLaboAssurance ? 'ri-shield-check-fill' : 'ri-shield-check-line'} mr-1`}></i>Assurance
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Observations cliniques</label>
                    <textarea value={bonLaboObs} onChange={(e) => setBonLaboObs(e.target.value)} className={textareaClass} rows={3} placeholder="Signes cliniques, éléments pouvant influencer les résultats..." maxLength={500} />
                  </div>

                  {/* Bouton Envoyer au labo */}
                  <div className="border-t border-gray-100 pt-5">
                    <button
                      onClick={() => setBonLaboSent(true)}
                      disabled={selectedAnalyses.length === 0}
                      className="w-full py-3 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                    >
                      <i className="ri-send-plane-fill"></i>
                      Envoyer le patient au laboratoire
                    </button>
                    {selectedAnalyses.length === 0 && (
                      <p className="text-xs text-gray-400 text-center mt-2">Sélectionnez au moins une analyse pour envoyer le patient au labo.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Lecture seule du bon (après envoi au labo) */}
              {bonLaboSent && (
                <div className="p-6 space-y-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div>
                      <p className="text-sm font-bold text-gray-900">Bon envoyé au laboratoire</p>
                      <p className="text-xs text-gray-500">Le patient peut se rendre au laboratoire avec ce bon</p>
                    </div>
                    <button
                      onClick={() => setBonLaboSent(false)}
                      className="ml-auto px-4 py-1.5 text-xs font-semibold text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-edit-line mr-1"></i>Modifier
                    </button>
                  </div>

                  <div className="p-5 bg-gray-50 rounded-xl border border-gray-200 space-y-4">
                    {/* Urgence */}
                    {bonLaboUrgence && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <i className="ri-alert-fill text-red-500 w-4 h-4 flex items-center justify-center"></i>
                        <span className="text-sm font-bold text-red-700">URGENT</span>
                      </div>
                    )}

                    {/* Analyses sélectionnées */}
                    <div>
                      <p className="text-xs text-gray-500 mb-2 font-semibold uppercase tracking-wide">Analyses demandées</p>
                      <div className="space-y-1">
                        {selectedAnalyses.map((id) => {
                          const isCustom = id.startsWith('custom-');
                          let label = id;
                          if (isCustom) {
                            const custom = customAnalyses.find((a) => a.id === id);
                            label = custom ? custom.label : id;
                          } else {
                            const item = CATEGORIES_ANALYSES.flatMap((c) => c.items).find((i) => i.id === id);
                            label = item ? item.label : id;
                          }
                          return (
                            <div key={id} className="flex items-center gap-2 py-1.5 px-3 bg-white rounded-lg border border-gray-100">
                              <i className="ri-checkbox-circle-fill text-clinic-blue w-3.5 h-3.5 flex items-center justify-center flex-shrink-0"></i>
                              <span className="text-sm text-gray-800">{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Observations cliniques */}
                    {bonLaboObs && (
                      <div className="border-t border-gray-200 pt-4">
                        <p className="text-xs text-gray-500 mb-2 font-semibold uppercase tracking-wide">Observations cliniques</p>
                        <p className="text-sm text-gray-700 bg-white p-3 rounded-lg border border-gray-100">{bonLaboObs}</p>
                      </div>
                    )}

                    {/* Date du bon */}
                    <div className="border-t border-gray-200 pt-4 flex items-center gap-2 text-xs text-gray-500">
                      <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      Bon émis le {new Date().toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                </div>
              )}

              {/* Si ni en édition ni envoyé, afficher placeholder */}
              {!bonLaboSent && !showBonLabo && (
                <div className="p-6 text-center">
                  <div className="py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <i className="ri-microscope-line text-3xl text-gray-300"></i>
                    <p className="text-sm text-gray-500 mt-2">Ajoutez un bon de laboratoire si des analyses sont nécessaires.</p>
                    <button
                      onClick={() => setShowBonLabo(true)}
                      className="mt-3 px-4 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-add-line mr-1"></i>Ajouter un bon
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ===== SECTION 9: FICHE DE SOIN ===== */}
            <div ref={sectionRefs['fiche-soin']} id="section-fiche-soin" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Fiche de Soin
                </h2>
                <button
                  onClick={() => setShowFicheSoin(!showFicheSoin)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                    showFicheSoin ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-clinic-blue text-white hover:bg-clinic-blue-dark'
                  }`}
                >
                  {showFicheSoin ? 'Retirer' : 'Ajouter une fiche de soin'}
                </button>
              </div>
              <div className="p-6">
                {showFicheSoin ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl mb-4 space-y-1">
                      <p className="text-sm text-blue-800 flex items-center gap-2">
                        <i className="ri-information-line w-4 h-4 flex items-center justify-center"></i>
                        Une fiche de surveillance sera automatiquement créée pour chaque soin. Les infirmiers pourront la remplir.
                      </p>
                      <p className="text-sm text-blue-800 flex items-center gap-2">
                        <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                        Date du soin : {new Date(dateConsultation).toLocaleDateString('fr-FR')} (définie automatiquement)
                      </p>
                    </div>
                    <div className="space-y-2">
                      {soins.map((s) => (
                        <div key={s.id} className="flex gap-2 items-start">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={s.description}
                              onChange={(e) => updateSoin(s.id, 'description', e.target.value)}
                              className={inputClass}
                              placeholder="Soin à administrer..."
                            />
                          </div>
                          {soins.length > 1 && (
                            <button onClick={() => removeSoin(s.id)} type="button" className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0 mt-1">
                              <i className="ri-close-line"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button onClick={addSoin} type="button" className="px-3 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-xs font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1">
                      <i className="ri-add-line"></i>Ajouter un soin
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                      <i className="ri-nurse-line text-3xl text-gray-300"></i>
                      <p className="text-sm text-gray-500 mt-2">Ajoutez une fiche de soin si des soins infirmiers sont nécessaires.</p>
                      <button
                        onClick={() => setShowFicheSoin(true)}
                        className="mt-3 px-4 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
                      >
                        <i className="ri-add-line mr-1"></i>Ajouter une fiche de soin
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ===== SECTION 10: SUIVI ===== */}
            <div ref={sectionRefs.suivi} id="section-suivi" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Suivi
                </h2>
              </div>
              <div className="p-6 space-y-5">
                <label className={labelClass}>Orientation du patient</label>
                <div className="flex gap-4 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="typeSuivi"
                      checked={typeSuivi === 'medecin'}
                      onChange={() => setTypeSuivi('medecin')}
                      className="w-4 h-4 text-clinic-blue focus:ring-clinic-blue cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Vers un médecin spécifique</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="typeSuivi"
                      checked={typeSuivi === 'departement'}
                      onChange={() => setTypeSuivi('departement')}
                      className="w-4 h-4 text-clinic-blue focus:ring-clinic-blue cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">Vers un département</span>
                  </label>
                </div>

                {typeSuivi === 'medecin' ? (
                  <select
                    value={suiviVers}
                    onChange={(e) => setSuiviVers(e.target.value)}
                    className={inputClass + ' appearance-none bg-white pr-8'}
                  >
                    <option value="">Sélectionner un médecin...</option>
                    {medecinsList.map((m) => (
                      <option key={m.id} value={`${m.prenom} ${m.nom}${m.specialite ? ` (${m.specialite})` : ''}`}>
                        {m.prenom} {m.nom}{m.specialite ? ` — ${m.specialite}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={suiviDepartement}
                    onChange={(e) => setSuiviDepartement(e.target.value)}
                    className={inputClass + ' appearance-none bg-white pr-8'}
                  >
                    <option value="">Sélectionner un département...</option>
                    {departements.map((dep) => (
                      <option key={dep} value={dep}>{dep}</option>
                    ))}
                  </select>
                )}
                {typeSuivi === 'departement' && suiviDepartement && (
                  <p className="mt-2 text-xs text-clinic-blue flex items-center gap-1">
                    <i className="ri-information-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    Tous les médecins du département <strong>{suiviDepartement}</strong> auront accès aux résultats.
                  </p>
                )}
              </div>
            </div>

            {/* ===== SECTION 11: PRESCRIPTION ===== */}
            <div ref={sectionRefs.prescription} id="section-prescription" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Prescription
                </h2>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        Ordonnance
                  </h3>

                  <div className="space-y-3">
                    <div className="space-y-3">
                      {medicamentsPrescrits.map((m, i) => (
                        <div key={m.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                          <div className="flex gap-2 items-start">
                            <div className="flex-1">
                              <MedicamentAutocomplete
                                value={m.nom}
                                onChange={(nom) => updateMedicamentPrescrit(m.id, 'nom', nom)}
                                placeholder={`Médicament ${i + 1}`}
                                className={inputClass}
                              />
                            </div>
                            {medicamentsPrescrits.length > 1 && (
                              <button onClick={() => removeMedicamentPrescrit(m.id)} type="button" className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0 mt-1">
                                <i className="ri-close-line"></i>
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3">
                            {/* Voie / forme */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 font-medium">Voie / Forme</span>
                              <select
                                value={m.forme}
                                onChange={(e) => updateMedicamentPrescrit(m.id, 'forme', e.target.value)}
                                className="px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none cursor-pointer"
                              >
                                {VOIES_GROUPES.map((g) => (
                                  <optgroup key={g.groupe} label={g.groupe}>
                                    {g.voies.map((v) => (
                                      <option key={v.key} value={v.key}>{v.label}</option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                            </div>
                            {/* Dosage / quantité — adapté à la voie */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 font-medium">{getVoie(m.forme).dosageLabel}</span>
                              {getVoie(m.forme).dosageType === 'count' ? (
                                <select
                                  value={m.dosageNb}
                                  onChange={(e) => updateMedicamentPrescrit(m.id, 'dosageNb', e.target.value)}
                                  className="px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none cursor-pointer"
                                >
                                  {['1', '2', '3', '4', '5', '6'].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={m.dosageNb}
                                  onChange={(e) => updateMedicamentPrescrit(m.id, 'dosageNb', e.target.value)}
                                  className="w-28 px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none"
                                  placeholder={getVoie(m.forme).dosagePlaceholder || 'ex: 1'}
                                />
                              )}
                            </div>
                            {/* Moments */}
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs text-gray-500 font-medium mr-1">×</span>
                              <label className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                                m.matin ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={m.matin}
                                  onChange={() => toggleMoment(m.id, 'matin')}
                                  className="hidden"
                                />
                                Matin
                              </label>
                              <label className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                                m.midi ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={m.midi}
                                  onChange={() => toggleMoment(m.id, 'midi')}
                                  className="hidden"
                                />
                                Midi
                              </label>
                              <label className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                                m.soir ? 'bg-sky-100 text-sky-800 border border-sky-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={m.soir}
                                  onChange={() => toggleMoment(m.id, 'soir')}
                                  className="hidden"
                                />
                                Soir
                              </label>
                            </div>
                            {/* Conditionnement (boîtes / plaquettes) */}
                            {getVoie(m.forme).showBoites && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 font-medium">Conditionnement</span>
                                <select
                                  value={m.nbBoites}
                                  onChange={(e) => updateMedicamentPrescrit(m.id, 'nbBoites', e.target.value)}
                                  className="px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none cursor-pointer"
                                >
                                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                                <select
                                  value={m.conditionnement || 'boite'}
                                  onChange={(e) => updateMedicamentPrescrit(m.id, 'conditionnement', e.target.value)}
                                  className="px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none cursor-pointer"
                                >
                                  <option value="boite">boîte(s)</option>
                                  <option value="plaquette">plaquette(s)</option>
                                </select>
                              </div>
                            )}
                            {getVoie(m.forme).showVoieInjection && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 font-medium">Voie</span>
                                <select
                                  value={m.voieInjection || ''}
                                  onChange={(e) => updateMedicamentPrescrit(m.id, 'voieInjection', e.target.value)}
                                  className="px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none cursor-pointer"
                                >
                                  <option value="">Choisir...</option>
                                  <option value="IV">IV (intraveineuse)</option>
                                  <option value="IM">IM (intramusculaire)</option>
                                  <option value="SC">SC (sous-cutanée)</option>
                                  <option value="ID">ID (intradermique)</option>
                                </select>
                              </div>
                            )}
                            {getVoie(m.forme).showSiteGouttes && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 font-medium">Localisation</span>
                                <select
                                  value={m.siteGouttes || ''}
                                  onChange={(e) => updateMedicamentPrescrit(m.id, 'siteGouttes', e.target.value)}
                                  className="px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none cursor-pointer"
                                >
                                  <option value="">Choisir...</option>
                                  <option value="yeux">Yeux</option>
                                  <option value="oreilles">Oreilles</option>
                                  <option value="nez">Nez</option>
                                </select>
                              </div>
                            )}
                            {getVoie(m.forme).showDissolution && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500 font-medium">Préparation</span>
                                <select
                                  value={m.dissolution || ''}
                                  onChange={(e) => updateMedicamentPrescrit(m.id, 'dissolution', e.target.value)}
                                  className="px-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-clinic-blue focus:outline-none cursor-pointer"
                                >
                                  <option value="">Choisir...</option>
                                  <option value="à dissoudre">À dissoudre</option>
                                  <option value="à prendre directement">À prendre directement</option>
                                </select>
                              </div>
                            )}
                          </div>
                          {getVoie(m.forme).showSiteApplication && (
                            <div>
                              <label className="text-xs text-gray-500 font-medium mb-1 block">Sur quelle zone / partie du corps ?</label>
                              <input
                                type="text"
                                value={m.siteApplication || ''}
                                onChange={(e) => updateMedicamentPrescrit(m.id, 'siteApplication', e.target.value)}
                                className={inputClass}
                                placeholder="Ex: sur la plaie, sur le bras, sur le visage..."
                              />
                            </div>
                          )}
                          {/* Durée + Instructions liées à ce médicament */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-500 font-medium mb-1 block">Durée du traitement</label>
                              <input
                                type="text"
                                value={m.duree}
                                onChange={(e) => updateMedicamentPrescrit(m.id, 'duree', e.target.value)}
                                className={inputClass}
                                placeholder="Ex: 7 jours"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 font-medium mb-1 block">Instructions</label>
                              <input
                                type="text"
                                value={m.instructions}
                                onChange={(e) => updateMedicamentPrescrit(m.id, 'instructions', e.target.value)}
                                className={inputClass}
                                placeholder="Ex: pendant les repas, au coucher..."
                              />
                            </div>
                          </div>
                          {/* Résumé posologie */}
                          <div className="text-xs text-gray-400 italic">
                            → {formatLigneOrdonnance(m)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button onClick={addMedicamentPrescrit} type="button" className="px-3 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-xs font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1">
                      <i className="ri-add-line"></i>Ajouter un médicament
                    </button>

                  </div>
                </div>

              </div>
            </div>

            {/* ===== SECTION: IMPRIMER ===== */}
            <div ref={sectionRefs.imprimer} id="section-imprimer" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Imprimer les documents
                </h2>
                <p className="text-sm text-gray-500 mt-1">Imprimez chaque document séparément avant de terminer la consultation.</p>
              </div>
              <div className="p-6 space-y-4">
                {/* Bon de Laboratoire */}
                {showBonLabo && selectedAnalyses.length > 0 ? (
                  <div className="bg-clinic-blue/10 border border-clinic-blue/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm">Bon de Laboratoire</h3>
                        <p className="text-xs text-gray-500">
                          {selectedAnalyses.length} analyse{selectedAnalyses.length > 1 ? 's' : ''}
                          {bonLaboUrgence && ' • URGENT'}
                          {bonLaboAssurance && ' • Assurance'}
                        </p>
                        {selectedAnalyses.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {selectedAnalyses.slice(0, 3).map((id) => {
                              const isCustom = id.startsWith('custom-');
                              let label = id;
                              if (isCustom) {
                                const custom = customAnalyses.find((a) => a.id === id);
                                label = custom ? custom.label : id;
                              } else {
                                const item = CATEGORIES_ANALYSES.flatMap((c) => c.items).find((i) => i.id === id);
                                label = item ? item.label : id;
                              }
                              return (
                                <span key={id} className="px-2 py-0.5 bg-white rounded-full text-xs text-clinic-blue-dark border border-clinic-blue/30">
                                  {label.length > 25 ? label.slice(0, 25) + '…' : label}
                                </span>
                              );
                            })}
                            {selectedAnalyses.length > 3 && (
                              <span className="px-2 py-0.5 text-xs text-clinic-blue">+{selectedAnalyses.length - 3} autres</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setPrintModalType('bon-labo')}
                      className="px-5 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
                    >
                      <i className="ri-printer-line"></i>
                      Imprimer
                    </button>
                  </div>
                ) : showBonLabo && selectedAnalyses.length === 0 ? (
                  <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-4 text-center">
                    <i className="ri-microscope-line text-2xl text-gray-300"></i>
                    <p className="text-sm text-gray-500 mt-1">Sélectionnez au moins une analyse pour imprimer le bon de laboratoire.</p>
                  </div>
                ) : null}

                {/* Ordonnance */}
                {medicamentsPrescrits.filter((m) => m.nom.trim()).length > 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm">Ordonnance</h3>
                        <p className="text-xs text-gray-500">
                          {medicamentsPrescrits.filter((m) => m.nom.trim()).length} médicament{medicamentsPrescrits.filter((m) => m.nom.trim()).length > 1 ? 's' : ''}
                        </p>
                        <div className="mt-1">
                          {medicamentsPrescrits.filter((m) => m.nom.trim()).slice(0, 2).map((m) => (
                            <p key={m.id} className="text-xs text-gray-600 truncate max-w-[280px]">{m.nom} — {doseCourte(m)}</p>
                          ))}
                          {medicamentsPrescrits.filter((m) => m.nom.trim()).length > 2 && (
                            <p className="text-xs text-amber-500">+{medicamentsPrescrits.filter((m) => m.nom.trim()).length - 2} autres</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setPrintModalType('ordonnance')}
                      className="px-5 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-all font-semibold text-sm cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
                    >
                      <i className="ri-printer-line"></i>
                      Imprimer
                    </button>
                  </div>
                ) : null}

                {/* Fiche de Soin */}
                {showFicheSoin && soins.filter((s) => s.description.trim()).length > 0 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm">Fiche de Soins</h3>
                        <p className="text-xs text-gray-500">
                          {soins.filter((s) => s.description.trim()).length} soin{soins.filter((s) => s.description.trim()).length > 1 ? 's' : ''}
                        </p>
                        <div className="mt-1">
                          {soins.filter((s) => s.description.trim()).slice(0, 2).map((s) => (
                            <p key={s.id} className="text-xs text-gray-600 truncate max-w-[280px]">
                              {s.date ? new Date(s.date).toLocaleDateString('fr-FR') : '—'} — {s.description}
                            </p>
                          ))}
                          {soins.filter((s) => s.description.trim()).length > 2 && (
                            <p className="text-xs text-blue-500">+{soins.filter((s) => s.description.trim()).length - 2} autres</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setPrintModalType('fiche-soin')}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold text-sm cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
                    >
                      <i className="ri-printer-line"></i>
                      Imprimer
                    </button>
                  </div>
                ) : null}

                {/* Aucun document disponible */}
                {(!showBonLabo || selectedAnalyses.length === 0) &&
                 medicamentsPrescrits.filter((m) => m.nom.trim()).length === 0 &&
                 (!showFicheSoin || soins.filter((s) => s.description.trim()).length === 0) && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                      <i className="ri-file-list-3-line text-2xl text-gray-400"></i>
                    </div>
                    <p className="text-sm text-gray-500 mt-3 font-medium">Aucun document à imprimer</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Ajoutez un bon de laboratoire, une ordonnance ou une fiche de soin pour pouvoir les imprimer.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ===== SECTION 12: FIN DE CONSULTATION ===== */}
            <div ref={sectionRefs.fin} id="section-fin" className="bg-white rounded-2xl border border-gray-200 overflow-hidden scroll-mt-28">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                    Fin de consultation
                </h2>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Prochain rendez-vous</label>
                    <input type="date" value={prochainRdv} onChange={(e) => setProchainRdv(e.target.value)} className={inputClass + ' max-w-xs'} />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Mot de fin</label>
                  <textarea
                    value={motFin}
                    onChange={(e) => setMotFin(e.target.value)}
                    className={textareaClass}
                    rows={3}
                    placeholder="Un petit mot pour conclure la consultation..."
                    maxLength={500}
                  />
                </div>

                <div className="border-t border-gray-100 pt-6 flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    onClick={() => navigate('/medecin/rendez-vous')}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-sm whitespace-nowrap cursor-pointer"
                  >
                    Retour (brouillon sauvegardé)
                  </button>
                  <button
                    onClick={handleTerminerConsultation}
                    disabled={saving}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all font-semibold text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    {saving ? (
                      <><i className="ri-loader-4-line animate-spin"></i>Sauvegarde...</>
                    ) : (
                      <><i className="ri-check-line"></i>Terminer la consultation</>
                    )}
                  </button>
                </div>
              </div>
            </div>
            </>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {/* Modal consultation en cours */}
      <ConsultationEnCoursModal
        isOpen={showConsultationEnCoursModal}
        onClose={handleCancelNouvelleConsultation}
        onConfirm={handleConfirmNouvelleConsultation}
        patientEnCours={consultationEnCoursPatient}
        nouveauPatient={patient ? `${patient.prenom} ${patient.nom}` : ''}
      />

      {/* Modales d'impression individuelles */}
      <PrintSingleDocumentModal
        isOpen={printModalType === 'bon-labo'}
        onClose={() => setPrintModalType(null)}
        documentTitle="Bon de Laboratoire"
      >
        <PrintableBonLaboratoire
          patientName={patient ? `${patient.prenom} ${patient.nom}` : ''}
          practitionerName={medecinInfo ? `${medecinInfo.prenom || ''} ${medecinInfo.nom || ''}`.trim() : (user?.user_metadata?.full_name || '')}
          practitionerTitle={medecinInfo?.specialite || ''}
          date={new Date(dateConsultation).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          analyses={(() => {
            const allIds = selectedAnalyses.filter((id) => !id.startsWith('custom-'));
            const customIds = selectedAnalyses.filter((id) => id.startsWith('custom-'));
            return [
              ...allIds.map((id) => {
                const item = CATEGORIES_ANALYSES.flatMap((c) => c.items).find((i) => i.id === id);
                return item ? item.label : id;
              }),
              ...customIds.map((id) => {
                const custom = customAnalyses.find((a) => a.id === id);
                return custom ? custom.label : id;
              }),
            ];
          })()}
          observations={bonLaboObs || undefined}
          urgences={bonLaboUrgence}
          soins={[]}
          constantes={{
            taille: taille || '',
            poids: poids || '',
            tension_arterielle: buildTensionSummary() || '',
            temperature: temperature || '',
            libelle: '',
          }}
          patientAge={patient ? String(new Date().getFullYear() - new Date(patient.date_naissance).getFullYear()) : ''}
          patientTelephone={patient?.telephone || ''}
          patientProfession=""
          patientAssure={bonLaboAssurance}
          medecinTraitant=""
          antecedentsMedicaux={antecedentsConnus || undefined}
        />
      </PrintSingleDocumentModal>

      <PrintSingleDocumentModal
        isOpen={printModalType === 'ordonnance'}
        onClose={() => setPrintModalType(null)}
        documentTitle="Ordonnance"
      >
        <PrintablePrescription
          patientName={patient ? `${patient.prenom} ${patient.nom}` : ''}
          practitionerName={medecinInfo ? `${medecinInfo.prenom || ''} ${medecinInfo.nom || ''}`.trim() : (user?.user_metadata?.full_name || '')}
          practitionerTitle={medecinInfo?.specialite || ''}
          date={new Date(dateConsultation).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          ordonnance={formatOrdonnanceDocument(medicamentsPrescrits)}
        />
      </PrintSingleDocumentModal>

      <PrintSingleDocumentModal
        isOpen={printModalType === 'fiche-soin'}
        onClose={() => setPrintModalType(null)}
        documentTitle="Fiche de Soins"
      >
        <PrintableFicheSoin
          patientName={patient ? `${patient.prenom} ${patient.nom}` : ''}
          practitionerName={medecinInfo ? `${medecinInfo.prenom || ''} ${medecinInfo.nom || ''}`.trim() : (user?.user_metadata?.full_name || '')}
          practitionerTitle={medecinInfo?.specialite || ''}
          date={new Date(dateConsultation).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          soins={soins}
        />
      </PrintSingleDocumentModal>

    </div>
  );
}